/**
 * RefundBookingDrawer — refund a CONFIRMED booking to a Bonsai Bar gift card,
 * the original card, or another rail recorded by hand (BON-147, BON-335).
 *
 * The amount is never client-supplied: the server computes it from the
 * booking's net cash, and the receipt state shows exactly what the response
 * returned rather than a client recomputation.
 *
 * A 409 `ALREADY_REFUNDED` is not a failure — another tab (or a double click
 * that beat this one's disabled button) already settled it — so it renders the
 * same "already refunded" outcome a fresh page load would show, not an error.
 *
 * The destination choice and the form itself live in their own files
 * (`RefundDestinationChoice`, `RefundForm`) as siblings of `RefundReceiptPanel`
 * — this drawer is back to being the thin shell it already was for the
 * receipt case, now doing the same for the form. `drawerHeader` stays here
 * because it is the one thing both states share.
 *
 * The remaining helper below is a plain (lowercase) function rather than a
 * component: it is used exactly once, from this one parent, so a separate
 * file per `react/no-multi-comp` would scatter the drawer for no reuse anyone
 * would call.
 */
import { Receipt, X } from 'lucide-react';
import { useRef } from 'react';

import type { BookingDetail, RefundMethod } from '../../api/bookingsV2';
import { useFocusTrap } from '../../hooks/useFocusTrap';

import { customerDisplayName } from './bookingDetailModel';
import { RefundForm } from './RefundForm';
import { RefundReceiptPanel, receiptTitle } from './RefundReceiptPanel';
import { useGiftPassesEnabled } from './use-gift-passes-enabled';
import { useRefundBookingSubmit } from './useRefundBookingSubmit';

/** The header's subtitle changes once the CSR steps off gift passes or the
 *  gift card: their promise ("issued", "emailed") does not apply to a refund
 *  that only records money moved elsewhere. */
function headerSubtitle(name: string, destination: RefundMethod): string {
  const base = `Full refund for ${name}. Partial refunds aren't available.`;
  if (destination === 'GIFT_CARD' || destination === 'GIFT_PASS') return base;
  return `${base} Nothing is minted or emailed here — record this only after the money has already moved.`;
}

function drawerHeader(
  booking: BookingDetail,
  title: string,
  subtitle: string,
  onClose: () => void,
  disabled: boolean,
) {
  const { money } = booking;
  return (
    // The clay header band from the CreateDrawer primitive, matched by hand
    // rather than by using it: that primitive brings a stepper and its own
    // token system, both scoped under `.nb-dashboards`, and the bookings area
    // deliberately sits outside that scope (the reason BookingStatusPill
    // exists). `bg-clay-500` is the same #db8030 as `--nb-clay-500`, and
    // resolves anywhere.
    <div className="bg-clay-500 px-6 pb-[18px] pt-5 text-white">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
            <Receipt size={18} />
          </span>
          <div className="min-w-0">
            {/* Ticket count only. The design showed a confirmation code here,
                but imported bookings have none and fall back to the raw UUID,
                which renders as an unreadable 36-character string across the
                band. The customer is named in the subline below. */}
            <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] opacity-70">
              {money.ticketsCount} {money.ticketsCount === 1 ? 'ticket' : 'tickets'}
            </div>
            <h2
              id="refund-drawer-title"
              className="mt-1.5 font-headline text-[26px] font-normal uppercase leading-[1.02] tracking-[0.01em] text-white"
            >
              {title}
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={disabled}
          aria-label="Close"
          // Translucent-white on the solid band, same treatment the primitive's
          // close control uses over the clay header.
          className="flex-shrink-0 rounded-lg border border-white/45 p-1.5 text-white hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={18} />
        </button>
      </div>
      {/* Names who is being refunded and states the full-only constraint up
          front, where the design puts it — the seats note at the foot of the
          form repeats the consequence, not the constraint. */}
      <p className="mt-3.5 text-[13px] leading-snug text-white/95">{subtitle}</p>
    </div>
  );
}

export function RefundBookingDrawer({
  booking,
  onClose,
  onRefunded,
}: {
  booking: BookingDetail;
  onClose: () => void;
  /** Fires once the server confirms the refund — issued OR already-settled —
   *  so the detail page can re-read the booking. The drawer stays open as its
   *  own receipt; it does not close itself. */
  onRefunded: () => void;
}) {
  const giftPassesEnabled = useGiftPassesEnabled();
  const {
    reason,
    setReason,
    giftPassesEnabled: offeredGiftPasses,
    destination,
    setDestination,
    reference,
    setReference,
    unavailableReasons,
    submitting,
    error,
    outcome,
    canSubmit,
    reasonRef,
    submit,
    dismiss,
  } = useRefundBookingSubmit(booking, onClose, onRefunded, giftPassesEnabled);
  const title = outcome ? receiptTitle(outcome) : 'Refund';
  const subtitle = outcome ? '' : headerSubtitle(customerDisplayName(booking), destination);
  // Net cash, not the receipt total — this is the figure the server mints, and
  // the two diverge on part-paid and imported bookings. `null` (an import with
  // no recorded payment) is not refundable; the action is gated upstream on
  // `hasRefundableAmount`, so 0 here only ever renders a disabled submit.
  const refundCents = booking.money.amountPaidCents ?? 0;
  const panelRef = useRef<HTMLDivElement>(null);
  // No autoFocusFirst: `useDialogKeyboard` already puts the caret in the
  // reason field, which is where a CSR wants it.
  useFocusTrap(panelRef);

  return (
    <div
      role="presentation"
      onClick={dismiss}
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="refund-drawer-title"
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl dark:bg-[#1a1f1c] sm:max-w-md lg:max-w-lg"
      >
        {drawerHeader(booking, title, subtitle, dismiss, submitting)}
        {outcome ? (
          <RefundReceiptPanel booking={booking} outcome={outcome} onDone={onClose} />
        ) : (
          <RefundForm
            booking={booking}
            refundCents={refundCents}
            giftPassesEnabled={offeredGiftPasses}
            destination={destination}
            onDestinationChange={setDestination}
            unavailableReasons={unavailableReasons}
            reason={reason}
            onReasonChange={setReason}
            reference={reference}
            onReferenceChange={setReference}
            error={error}
            submitting={submitting}
            canSubmit={canSubmit}
            reasonRef={reasonRef}
            onSubmit={submit}
            onCancel={dismiss}
          />
        )}
      </div>
    </div>
  );
}
